import React from 'react';
import styled, { css } from '@xstyled/styled-components';
import { jumpAnimation } from '~/ui/animation';
import { TrainingState, useTrainingStore } from '~/training/state';
import { select } from '~/lib/select';
import { FC, useEffect, useState } from 'react';
import { useMediaPlayerStore } from '~/ui/video-player/MediaPlayerProvider';
import { useSequenceStore } from '~/training/sequence/SequenceStore';
import { useAudioEffect } from '~/ui/audio-effect/useAudioEffect';
import audioEffectRightAnswer from '~/assets/mp3/right_answer.mp3';
import audioEffectWrongAnswer from '~/assets/mp3/wrong_answer.mp3';
import audioEffectClick from '~/assets/mp3/light_switches.mp3';
import { useGlobalMedia } from '~/ui/video-player/useGlobalMedia';
import { useQuizStore } from '../quiz/QuizStore';
import { useGlobalMediaStore } from '~/ui/video-player/GlobalMediaProvider';

const selectorVisualFeedback = (s: TrainingState) =>
  select(s, 'visualFeedback', 'auditoryFeedback');

const StyledQuizInstaComponentButton = styled.buttonBox<{
  variant?: 'correct' | 'wrong' | 'default';
}>`
  width: 47%;
  padding: 10px 55px;
  text-align: center;
  border-radius: 50px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  font-weight: bold;
  border-width: 2px;
  background-color: white;
  color: black;
  border-color: greyMed;
  box-shadow: 0 4px 0 var(--color-grey-md);

  &:focus {
    border: 2px solid blue;
    outline-offset: 2px;
  }
  &:focus::after {
    content: '';
    display: block;
    position: absolute;
    border-radius: 25px;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    border: 2px solid white;
    pointer-events: none;
  }
  &:disabled {
    cursor: not-allowed;
    background-color: greyLight;
    > * {
      opacity: 0.5;
    }
  }
  ${p => {
    const { visualFeedback } = useTrainingStore(selectorVisualFeedback);
    const sec = visualFeedback ? '0.7s' : '0s';

    return p.variant === 'correct'
      ? css`
          color: greenDark;
          border-color: greenDark;
          box-shadow: 0 4px 0 var(--color-green-dk);

          &:before {
            content: '';
            color: white;
            position: absolute;
            width: 28px;
            height: 28px;
            border-radius: 14px;
            background-color: greenDark;
            top: -14px;
            right: -14px;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          &:after {
            content: '✔';
            color: white;
            position: absolute;
            width: 28px;
            height: 28px;
            top: -14px;
            right: -14px;
            display: flex;
            align-items: center;
            justify-content: center;
            animation: ${jumpAnimation('1px', '-4px', '1px')} ${sec} linear
              infinite;
          }
        `
      : '';
  }}
  ${p => {
    const { visualFeedback } = useTrainingStore(selectorVisualFeedback);
    const sec = visualFeedback ? '0.7s' : '0s';

    return p.variant === 'wrong'
      ? css`
          color: redDark;
          border-color: redDark;
          box-shadow: 0 4px 0 var(--color-red-dk);

          &:after {
            content: '!';
            color: white;
            position: absolute;
            width: 28px;
            height: 28px;
            border-radius: 14px;
            background-color: redDark;
            top: -14px;
            right: -14px;
            display: flex;
            align-items: center;
            justify-content: center;
            animation: ${jumpAnimation('0px', '5px', '0px')} ${sec} linear
              infinite;
          }
        `
      : '';
  }}
`;

interface Props {
  id: string;
  group: string;
  setQuestionAnswered: (state: boolean) => void;
  text?: string;
  correct?: boolean;
  feedback?: string;

  /** Id of an audio element that needs to be played for the button to be enabled */
  linkedAudioId?: string;
  feedbackAudios?: {
    mediaUrl: string;
    subtitlesUrl: string;
  } | null;
}

export const QuizInstaComponentButton: FC<Props> = ({
  id,
  setQuestionAnswered,
  group,
  correct,
  feedback,
  feedbackAudios,
  text,
  children
}) => {
  const { saveQuestionAnswers } = useTrainingStore();
  const { auditoryFeedback } = useTrainingStore(selectorVisualFeedback);
  const { reset, loadMedia, status } = useMediaPlayerStore();
  const { addBlocker, removeBlocker, setAnswer, setLastAnswer, clearAnswer } =
    useSequenceStore();
  const [disabledButtons, setDisabledButtons] = useState(false);
  const { setQuizFeedback, getQuizFeedback } = useQuizStore();
  const [selectedAnswer, setSelectedAnswer] = useState<
    'correct' | 'default' | 'wrong'
  >('default');
  const [playAudioEffectRightAnswer] = useAudioEffect(audioEffectRightAnswer);
  const [playAudioEffectWrongAnswer] = useAudioEffect(audioEffectWrongAnswer);
  const [playAudioEffectClick] = useAudioEffect(audioEffectClick);

  const globalMedia = useGlobalMediaStore();
  const globalPlayer = globalMedia?.player;

  useEffect(() => {
    if (globalPlayer) {
      setDisabledButtons(true);
    } else {
      setDisabledButtons(false);
    }
  }, [globalPlayer]);

  useGlobalMedia();

  useEffect(() => {
    if (selectedAnswer == 'default') {
      addBlocker(group);
      return () => removeBlocker(group);
    }
  }, [selectedAnswer]);

  useEffect(() => {
    setTimeout(() => {
      if (status === 'nomedia') {
        removeBlocker(group);
      }
    }, 1500);
  }, [status]);

  const click = () => {
    saveQuestionAnswers(id);

    setQuestionAnswered(true);

    if (selectedAnswer == 'default') {
      reset();

      feedback && setQuizFeedback(feedback, group);

      setAnswer(group, id);
      removeBlocker(group);

      if (feedbackAudios && feedbackAudios.mediaUrl) {
        loadMedia(
          feedbackAudios.mediaUrl,
          feedbackAudios?.subtitlesUrl,
          'audio',
          true,
          0
        );
      }

      if (correct) {
        if (auditoryFeedback) {
          playAudioEffectRightAnswer();
        }
        setSelectedAnswer('correct');
        setLastAnswer('correct');
      } else {
        if (auditoryFeedback) {
          playAudioEffectWrongAnswer();
        }
        setSelectedAnswer('wrong');
        setLastAnswer('wrong');
      }

      if (auditoryFeedback) {
        playAudioEffectClick();
      }
    } else {
      //Button is selected and we need to return the state back
      setSelectedAnswer('default');
      setLastAnswer('default');
      const currentFeedback = getQuizFeedback(group);
      if (feedback == currentFeedback) setQuizFeedback('', group);
      clearAnswer(group, id);
      setQuestionAnswered(false);
    }
  };

  const content = text ? text : children;

  return (
    <StyledQuizInstaComponentButton
      variant={selectedAnswer}
      onClick={click}
      disabled={disabledButtons}
      tabIndex={0}
    >
      {content}
    </StyledQuizInstaComponentButton>
  );
};
